/* eslint-disable camelcase */
import { Box, Container, Stack } from '@mui/material';
import { memo, useCallback, useMemo } from 'react';
import useInspection from 'hooks/useInspection';
import PositiveButton from 'components/cmn/PositiveButton';
import { countByArray, handleGetClassificationEndMsg, handleSortGroup } from 'libs/mindUtils';
import { IClassificationProps, ToggleValue } from 'interface/inspection/InspectionInfo';
import { IInspectionIpsativesProps } from 'interface/project/projectInfo';

interface IClassificationGuideCmnEndProps {
  checkType: string;
}
function ClassificationGuideCmnEnd({ checkType }: IClassificationGuideCmnEndProps): JSX.Element {
  const {
    inspectionFirstClassification,
    inspectionSecondClassification,
    inspectionDetail,
    changeClassification,
    changeProgressInfo,
    // changeInspectionDetail2Info, // 제거됨 - 새로 만들 예정
    // changeInspectionDetail3Info, // 제거됨 - 새로 만들 예정
    // changeInspectionDetail3Info2, // 제거됨 - 새로 만들 예정
    // changeInspectionFinalStandard, // 제거됨 - 새로 만들 예정
    changeDetailProgressInfo,
    changeIsClassificationGuideStart2_1,
    initInspectionThirdClassification,
    initInspectionEndClassification,
    initInspectionFinalBase,
  } = useInspection();

  // 1단계 분류 결과: inspectionFirstClassification에서 가져오기
  const inspectionResult = useMemo(() => {
    return [
      ...inspectionFirstClassification.NNTemp,
      ...inspectionFirstClassification.NTemp,
      ...inspectionFirstClassification.ATemp,
      ...inspectionFirstClassification.AATemp,
    ];
  }, [inspectionFirstClassification]);

  // 2단계 분류 결과: inspectionSecondClassification에서 가져오기
  const inspectionResult2Info = useMemo(
    () => ({
      // NN 그룹의 모든 항목 합치기
      NNInfo: [
        ...inspectionSecondClassification.NNTempNN,
        ...inspectionSecondClassification.NNTempN,
        ...inspectionSecondClassification.NNTempA,
        ...inspectionSecondClassification.NNTempAA,
      ],
      // N 그룹의 모든 항목 합치기
      NInfo: [
        ...inspectionSecondClassification.NTempNN,
        ...inspectionSecondClassification.NTempN,
        ...inspectionSecondClassification.NTempA,
        ...inspectionSecondClassification.NTempAA,
      ],
      // A 그룹의 모든 항목 합치기
      AInfo: [
        ...inspectionSecondClassification.ATempNN,
        ...inspectionSecondClassification.ATempN,
        ...inspectionSecondClassification.ATempA,
        ...inspectionSecondClassification.ATempAA,
      ],
      // AA 그룹의 모든 항목 합치기
      AAInfo: [
        ...inspectionSecondClassification.AATempNN,
        ...inspectionSecondClassification.AATempN,
        ...inspectionSecondClassification.AATempA,
        ...inspectionSecondClassification.AATempAA,
      ],
    }),
    [inspectionSecondClassification],
  );

  const nextCondition: Partial<ToggleValue>[] = useMemo(() => ['NN', 'N', 'A', 'AA'], []);
  const checkPassArray = useCallback(
    (passArray: IClassificationProps[], expectedLength: number): boolean => {
      // 모든 항목이 분류되었는지 확인 (배열 길이가 예상 길이와 같고, 모든 항목이 유효한 값인지 확인)
      return passArray.length === expectedLength && passArray.every(passElement => nextCondition.includes(passElement.value));
    },
    [nextCondition],
  );

  const passInfoMap = useMemo(
    () => ({
      '1': inspectionResult,
      '2_1': inspectionResult2Info.AAInfo,
      '2_2': inspectionResult2Info.AInfo,
      '2_3': inspectionResult2Info.NNInfo,
      '2_4': inspectionResult2Info.NInfo,
    }),
    [
      inspectionResult,
      inspectionResult2Info.AAInfo,
      inspectionResult2Info.AInfo,
      inspectionResult2Info.NInfo,
      inspectionResult2Info.NNInfo,
    ],
  );
  
  // 각 단계별 예상 항목 수
  const expectedLengthMap = useMemo(
    () => ({
      '1': inspectionDetail.inspectionIpsatives.length,
      '2_1': inspectionFirstClassification.AATemp.length,
      '2_2': inspectionFirstClassification.ATemp.length,
      '2_3': inspectionFirstClassification.NNTemp.length,
      '2_4': inspectionFirstClassification.NTemp.length,
    }),
    [
      inspectionDetail.inspectionIpsatives.length,
      inspectionFirstClassification.AATemp.length,
      inspectionFirstClassification.ATemp.length,
      inspectionFirstClassification.NNTemp.length,
      inspectionFirstClassification.NTemp.length,
    ],
  );
  
  const isPass = useMemo(
    () => {
      const passArray = passInfoMap[checkType as keyof typeof passInfoMap] || [];
      const expectedLength = expectedLengthMap[checkType as keyof typeof expectedLengthMap] || 0;
      return checkPassArray(passArray, expectedLength);
    },
    [checkPassArray, checkType, passInfoMap, expectedLengthMap],
  );
  const NNInfo = useMemo(() => {
    return inspectionResult.filter((_obj1: IClassificationProps) => _obj1.value === 'NN');
  }, [inspectionResult]);
  const NInfo = useMemo(() => {
    return inspectionResult.filter((_obj1: IClassificationProps) => _obj1.value === 'N');
  }, [inspectionResult]);
  const AInfo = useMemo(() => {
    return inspectionResult.filter((_obj1: IClassificationProps) => _obj1.value === 'A');
  }, [inspectionResult]);
  const AAInfo = useMemo(() => {
    return inspectionResult.filter((_obj1: IClassificationProps) => _obj1.value === 'AA');
  }, [inspectionResult]);
  const handleConfirmClassification1 = useCallback(async () => {
    if (isPass && checkType === '1') {
      // TODO: 새로 만들 예정
      // const arrayCondition = (obj: IInspectionIpsativesProps) => ({
      //   id: obj.id,
      //   inspectionId: obj.inspectionId,
      //   content: obj.content,
      //   saveFileName: obj.saveFileName,
      //   regId: obj.regId,
      // });
      // const NNtemp = NNInfo.map(arrayCondition);
      // const Ntemp = NInfo.map(arrayCondition);
      // const Atemp = AInfo.map(arrayCondition);
      // const AAtemp = AAInfo.map(arrayCondition);
      await changeClassification('2_1');
      await changeIsClassificationGuideStart2_1(false); // 2_1 가이드 화면을 먼저 보여주기 위해 false로 설정
      await changeProgressInfo(0);
      // await changeInspectionDetail2Info({
      //   NNInfo: NNtemp || [],
      //   NInfo: Ntemp || [],
      //   AInfo: Atemp || [],
      //   AAInfo: AAtemp || [],
      // });
    }
  }, [changeClassification, changeProgressInfo, changeIsClassificationGuideStart2_1, checkType, isPass]);

  const handleConfirmClassification2_1 = useCallback(async () => {
    if (isPass && checkType === '2_1') {
      await changeClassification('2_2');
      await changeDetailProgressInfo(0);
    }
  }, [changeClassification, changeDetailProgressInfo, checkType, isPass]);

  const handleConfirmClassification2_2 = useCallback(async () => {
    if (isPass && checkType === '2_2') {
      await changeClassification('2_3');
      await changeDetailProgressInfo(0);
    }
  }, [changeClassification, changeDetailProgressInfo, checkType, isPass]);

  const handleConfirmClassification2_3 = useCallback(async () => {
    if (isPass && checkType === '2_3') {
      await changeClassification('2_4');
      await changeDetailProgressInfo(0);
    }
  }, [changeClassification, changeDetailProgressInfo, checkType, isPass]);

  const handleConfirmClassification2_4 = useCallback(async () => {
    if (isPass && checkType === '2_4') {
      const totalItems = inspectionDetail.inspectionIpsatives.length;
      const twentyPercentThreshold = Math.ceil(totalItems * 0.2);

      // inspectionSecondClassification의 모든 그룹을 확인하여 20% 넘는 그룹 찾기
      const groupsOver20Percent: string[] = [];
      const secondClassificationKeys: (keyof typeof inspectionSecondClassification)[] = [
        'NNTempNN',
        'NNTempN',
        'NNTempA',
        'NNTempAA',
        'NTempNN',
        'NTempN',
        'NTempA',
        'NTempAA',
        'ATempNN',
        'ATempN',
        'ATempA',
        'ATempAA',
        'AATempNN',
        'AATempN',
        'AATempA',
        'AATempAA',
      ];

      secondClassificationKeys.forEach(key => {
        const group = inspectionSecondClassification[key];
        if (group.length >= twentyPercentThreshold) {
          groupsOver20Percent.push(key);
        }
      });

      console.log('20% 넘는 그룹들:', groupsOver20Percent);

      // 전체 문항의 수가 50개 이상일 경우 && 전체 문항 수의 20%가 넘는 리스트가 있으면 3차 분류 진행
      if (totalItems >= 50 && groupsOver20Percent.length > 0) {
        // 3차 분류 초기화: 20% 넘는 그룹들에 대해 4개의 하위 그룹 생성
        await initInspectionThirdClassification(groupsOver20Percent);
        await changeClassification('3');
        await changeDetailProgressInfo(0);
      } else {
        // 최종 분류로 이동
        // 전체 문항의 수가 50개 미만인 경우 || 각 그룹에 입력된 문항수가 전체 문항 수의 20%가 넘는 리스트가 없을때
        // inspectionEndClassification 초기화: 2차 분류 결과를 그대로 복사 (3단계를 거치지 않았으므로)
        await initInspectionEndClassification();
        // inspectionFinalBase 초기화: grid를 사용해서 방 구조 생성
        const { grid } = inspectionDetail || {};
        if (grid && typeof grid === 'string' && grid.trim() !== '') {
          await initInspectionFinalBase(grid);
        }
        await changeClassification('final');
        await changeDetailProgressInfo(0);
      }
    }
  }, [
    changeClassification,
    changeDetailProgressInfo,
    checkType,
    inspectionDetail.inspectionIpsatives.length,
    inspectionDetail.grid,
    inspectionSecondClassification,
    isPass,
    initInspectionThirdClassification,
    initInspectionEndClassification,
    initInspectionFinalBase,
  ]);

  const classificationHandlers: Record<string, () => Promise<void>> = {
    '1': handleConfirmClassification1,
    '2_1': handleConfirmClassification2_1,
    '2_2': handleConfirmClassification2_2,
    '2_3': handleConfirmClassification2_3,
    '2_4': handleConfirmClassification2_4,
  };
  const handleNextClassification = () => {
    classificationHandlers[checkType]?.();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        py: 4,
      }}
    >
      <Box
        sx={{
          p: 4,
          width: '100%',
          maxWidth: { xs: '100%', md: '1536px' },
          height: { xs: 'auto', md: '864px' },
        }}
      >
        <Container maxWidth="xl" sx={{ height: 'calc(100%)' }}>
          <Container maxWidth="lg" sx={{ p: 0, height: 'calc(100% - 84px)' }}>
            <Stack
              direction="column"
              justifyContent="center"
              alignContent="center"
              alignItems="center"
              sx={{ height: '100%' }}
            >
              <Box
                sx={{
                  fontSize: { xs: '23px', sm: '48px', md: '52px' },
                  fontWeight: 900,
                  textAlign: 'center',
                }}
              >
                {handleGetClassificationEndMsg(checkType)[0]}
              </Box>
              <Box sx={{ height: 20 }} />
              <Box
                sx={{
                  fontSize: { xs: '23px', sm: '48px', md: '52px' },
                  fontWeight: 900,
                  textAlign: 'center',
                }}
              >
                {handleGetClassificationEndMsg(checkType)[1]}
              </Box>
            </Stack>
          </Container>
        </Container>
      </Box>
      {/* 하단 버튼 영역 높이만큼 여백 추가 (버튼 높이 52px + 패딩 32px = 약 84px) */}
      <Box sx={{ height: { xs: 84, sm: 84 } }} />
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100%',
          bgcolor: 'background.paper',
          boxShadow: '0px -2px 10px rgba(0, 0, 0, 0.1)',
          py: 2,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <PositiveButton
          variant="contained"
          onClick={handleNextClassification}
          sx={{ width: { xs: 130 }, height: 52, typography: 's14w500' }}
          title="다음"
        />
      </Box>
    </Box>
  );
}

export default memo(ClassificationGuideCmnEnd);
